import { c as _c } from "react/compiler-runtime";
import { FormSelect, Hint, LocaleEnum } from '@systemeio/ui';
import { useMemo } from 'react';
import TransLoco from 'shared/components/trans-loco';
import { useSenderEmailValidList } from 'shared/components/sender-email-select/hooks/use-sender-email-valid-list';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useSubstituteEmails } from '../hooks/use-substitute-emails';
const getHelpArticleLink = (locale?: LocaleEnum) => {
  switch (locale) {
    case LocaleEnum.DUTCH:
      return 'https://help-nl.systeme.io/article/1394-is-het-mogelijk-om-een-prive-e-mailadres-te-gebruiken-om-e-mails-te-verzenden-vanuit-systeme-io';
    case LocaleEnum.DEUTSCH:
      return 'https://help-de.systeme.io/article/1766-ist-es-moglich-eine-private-e-mail-adresse-zu-verwenden-um-e-mails-von-systemeio-zu-versenden';
    case LocaleEnum.PORTUGUESE:
      return 'https://help-pt.systeme.io/article/743-e-possivel-usar-um-endereco-de-email-particular-para-enviar-emails-na-systeme-io';
    case LocaleEnum.ITALIAN:
      return 'https://help-it.systeme.io/article/478-non-posso-usare-un-indirizzo-yahoo-per-inviare-le-mie-e-mail';
    case LocaleEnum.JAPANESE:
      return 'https://help-jp.systeme.io/article/2410-is-it-possible-to-use-a-private-email-address-to-send-emails-from-systemeio';
    case LocaleEnum.CHINESE:
      return 'https://help-zh.systeme.io/article/3218-is-it-possible-to-use-a-private-email-address-to-send-emails-from-systeme-io';
    case LocaleEnum.ARABIC:
      return 'https://help-ar.systeme.io/article/2731-is-its-possible-to-use-private-email-address-to-send-emails-from-systeme-io';
    case LocaleEnum.FRENCH:
      return 'https://aide.systeme.io/article/29-est-il-possible-utiliser-une-adresse-email-personnelle-pour-envoyer-des-emails-sur-systemeio';
    case LocaleEnum.SPANISH:
      return 'https://help-es.systeme.io/article/878-no-puedo-usar-una-direccion-de-correo-electronico-de-gmail-yahoo-para-enviar-mis-correos-electronicos';
    case LocaleEnum.HINDI:
      return 'https://help-hi.systeme.io/article/3264-is-it-possible-to-use-a-private-email-address-to-send-emails-from-systeme-io';
    default:
      return 'https://help.systeme.io/article/272-is-it-possible-to-use-a-private-email-address-to-send-emails-from-systemeio';
  }
};
namespace SenderEmailInput {
  export interface EmailOption {
    id: string;
    caption: string;
  }
  export interface Props {
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
    isPreFetching?: boolean;
  }
}
export function SenderEmailSelect(t0) {
  const $ = _c(27);
  let onChange;
  let rest;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = onChange;
    $[2] = rest;
    $[3] = value;
  } else {
    onChange = $[1];
    rest = $[2];
    value = $[3];
  }
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    substituteEmails
  } = useSubstituteEmails();
  const {
    data: senderEmailValidList
  } = useSenderEmailValidList();
  const userAccountEmail = user?.mainUser?.user.email || "";
  const substituteEmail = value ? substituteEmails?.[value] : substituteEmails?.[userAccountEmail];
  let verifiedEmails;
  if ($[4] !== senderEmailValidList || $[5] !== userAccountEmail) {
    verifiedEmails = senderEmailValidList?.map(_temp) || [];
    if (userAccountEmail && !verifiedEmails.some(item => item.id === userAccountEmail)) {
      verifiedEmails.push({
        id: userAccountEmail,
        caption: userAccountEmail
      });
    }
    $[4] = senderEmailValidList;
    $[5] = userAccountEmail;
    $[6] = verifiedEmails;
  } else {
    verifiedEmails = $[6];
  }
  const emailOptions = verifiedEmails;
  let t1;
  if ($[7] !== substituteEmail || $[8] !== t || $[9] !== user?.dashboardLocale) {
    t1 = substituteEmail ? <TransLoco t={t} i18nKey="dashboard.settings.mailing.sender_email.description" values={{
      substituteEmail
    }} components={{
      a: <a href={getHelpArticleLink(user?.dashboardLocale)} target="_blank" rel="noopener noreferrer" className="primary-link" />,
      b: <span className="text-brand-primary" />
    }} /> : undefined;
    $[7] = substituteEmail;
    $[8] = t;
    $[9] = user?.dashboardLocale;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== t) {
    t2 = t("dashboard.settings.mailing.sender_email_address_popover_title");
    $[11] = t;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t) {
    t3 = t("dashboard.settings.mailing.sender_email_address_title");
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== t2 || $[16] !== t3) {
    t4 = <Hint label={t2}>{t3}</Hint>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== onChange) {
    t5 = data => onChange(data || "");
    $[18] = onChange;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== emailOptions || $[21] !== rest || $[22] !== t1 || $[23] !== t4 || $[24] !== t5 || $[25] !== value) {
    t6 = <FormSelect descriptionClassName="text-text-primary opacity-100" description={t1} label={t4} data={emailOptions} value={value} onChange={t5} withoutCloseIcon={true} {...rest} />;
    $[20] = emailOptions;
    $[21] = rest;
    $[22] = t1;
    $[23] = t4;
    $[24] = t5;
    $[25] = value;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}
function _temp(t0) {
  const {
    email
  } = t0;
  return {
    id: email,
    caption: email
  };
}